import offlineDb from './db';
import { registerMapping, resolveId } from './tempIdManager';
import { isNetworkError } from '../auth';
import { connectivityMonitor } from './connectivityMonitor';

// Counted only when the server answered with a 5xx (see _processItem).
const MAX_ATTEMPTS = 3;
const REPLAY_TIMEOUT_MS = 30000;
const ITEM_GAP_MS = 500;
const MAX_PASSES = 5;
const SYNCED_RETENTION_MS = 24 * 60 * 60 * 1000;
const MAPPING_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

// Replays that fail with a 400 because the operation already applied on a
// previous (response-lost) attempt are successes, not failures.
const IDEMPOTENT_OK = [
  { when: (i) => i.operationType === 'sign', re: /already signed/i },
  { when: (i) => i.entityType === 'invoicePayment', re: /already recorded/i },
];

function priorityFor(operationType) {
  if (operationType === 'create' || operationType === 'check_in') return 1;
  if (operationType === 'sign') return 3;
  if (operationType === 'audit') return 4;
  return 2; // update / payment / delete
}

class SyncQueueService {
  constructor() {
    this._processing = false;
    this._subscribers = new Set();
  }

  subscribe(callback) {
    this._subscribers.add(callback);
    return () => this._subscribers.delete(callback);
  }

  _emit(event, data) {
    for (const cb of this._subscribers) {
      try { cb(event, data); } catch { /* ignore */ }
    }
  }

  async enqueue(operation) {
    const { operationType, entityType, entityId, tempId, method, endpoint, payload, methodName, dependsOn } = operation;

    const priority = priorityFor(operationType);

    // Coalescence: updates to the same entity MERGE into the existing pending
    // entry's payload (each PATCH is a partial update; replacing would drop
    // earlier fields — e.g. an offline check-in followed by the copay PATCH).
    if (operationType === 'update' && entityId) {
      const existing = await offlineDb.syncQueue
        .where('entityId').equals(entityId)
        .filter(item => item.status === 'pending' && item.operationType === 'update' && item.entityType === entityType)
        .first();

      if (existing) {
        await offlineDb.syncQueue.update(existing.localId, {
          payload: { ...existing.payload, ...payload },
          endpoint,
          updatedAt: Date.now(),
        });
        this._emit('queue:updated', { localId: existing.localId });
        return existing.localId;
      }
    }

    const localId = await offlineDb.syncQueue.add({
      operationType,
      entityType,
      entityId: entityId || null,
      tempId: tempId || null,
      method,
      endpoint,
      payload,
      status: 'pending',
      priority,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      attempts: 0,
      lastError: null,
      dependsOn: dependsOn || null,
      methodName: methodName || null,
      idempotencyKey: crypto.randomUUID(),
      uncertain: false,
    });

    this._emit('queue:added', { localId });
    return localId;
  }

  // Merge additional fields into a pending create's payload (offline PATCH of
  // a temp-id entity folds into its unsent create).
  async mergeIntoPendingCreate(tempId, patchPayload) {
    const item = await this.findPendingCreateByTempId(tempId);
    if (!item) return false;
    await offlineDb.syncQueue.update(item.localId, {
      payload: { ...item.payload, ...patchPayload },
      updatedAt: Date.now(),
    });
    this._emit('queue:updated', { localId: item.localId });
    return true;
  }

  async findPendingCreateByTempId(tempId) {
    if (!tempId) return null;
    return offlineDb.syncQueue
      .where('tempId').equals(tempId)
      .filter(i => i.status === 'pending' && i.operationType === 'create')
      .first();
  }

  // Remove pending items for an entity (offline DELETE of a temp entity
  // cancels its unsent create and updates).
  async removePendingForEntity(entityIdOrTempId) {
    if (!entityIdOrTempId) return 0;
    const byEntity = await offlineDb.syncQueue
      .where('entityId').equals(entityIdOrTempId)
      .filter(i => i.status === 'pending')
      .primaryKeys();
    const byTemp = await offlineDb.syncQueue
      .where('tempId').equals(entityIdOrTempId)
      .filter(i => i.status === 'pending')
      .primaryKeys();
    const keys = [...new Set([...byEntity, ...byTemp])];
    if (keys.length) await offlineDb.syncQueue.bulkDelete(keys);
    return keys.length;
  }

  // isHealthy (optional): checked before every item; the drain stops as soon
  // as it reports false.
  async processQueue(originalRequest, { isHealthy = null } = {}) {
    if (this._processing) return;
    this._processing = true;
    this._halt = false;
    this._emit('sync:start', {});

    try {
      // Rows stuck in_progress are stale (crash/reload mid-sync). Their
      // request may or may not have landed — reset to pending and mark
      // uncertain so creates get probed before being re-sent.
      await offlineDb.syncQueue
        .where('status').equals('in_progress')
        .modify({ status: 'pending', uncertain: true });

      let pass = 0;
      let totalProcessed = 0;

      // Multi-pass: an item whose dependency resolves during a pass gets its
      // chance on the next pass instead of waiting for the next reconnect.
      for (;;) {
        const pending = await offlineDb.syncQueue
          .where('status').equals('pending')
          .sortBy('localId');
        pending.sort((a, b) => a.priority - b.priority || a.localId - b.localId);

        if (!pending.length) break;

        let processed = 0;
        let skipped = 0;

        for (const item of pending) {
          if (this._halt || (isHealthy && !isHealthy())) break;
          if (item.dependsOn) {
            const dep = await this._dependencyState(item.dependsOn);
            if (dep === 'failed' || dep === 'missing') {
              await offlineDb.syncQueue.update(item.localId, {
                status: 'failed',
                lastError: `Depends on ${item.dependsOn} which ${dep === 'failed' ? 'failed to sync' : 'is missing'}`,
                updatedAt: Date.now(),
              });
              this._emit('item:failed', { localId: item.localId, error: 'dependency failed' });
              continue;
            }
            if (dep === 'unresolved') {
              skipped++;
              continue;
            }
          }

          await this._processItem(item, originalRequest);
          processed++;
          totalProcessed++;
          await new Promise(r => setTimeout(r, ITEM_GAP_MS));
          this._emit('sync:progress', { processed: totalProcessed, total: pending.length });
        }

        pass++;
        if (this._halt || (isHealthy && !isHealthy())) break;
        if (!skipped || !processed || pass >= MAX_PASSES) break;
      }

      this._emit('sync:complete', { processed: totalProcessed, total: totalProcessed });
    } catch (e) {
      console.error('[SyncQueue] processQueue error:', e);
      this._emit('sync:error', { error: e.message });
    } finally {
      this._processing = false;
      this.pruneSyncQueue().catch(() => {});
    }
  }

  async _processItem(item, originalRequest) {
    await offlineDb.syncQueue.update(item.localId, { status: 'in_progress' });

    try {
      // A create whose previous attempt may have landed (response lost, or
      // found in_progress after a crash) is probed before being re-sent —
      // there is no server-side unique constraint to stop a duplicate.
      if (item.uncertain && item.operationType === 'create') {
        const found = await this._probeExisting(item, originalRequest);
        if (found) {
          await this._markSynced(item, found);
          return;
        }
        await offlineDb.syncQueue.update(item.localId, { uncertain: false });
      }

      // Remap temp IDs in endpoint and payload
      let endpoint = await this._remapEndpoint(item.endpoint, item.dependsOn);
      let payload = item.payload;

      if (payload && typeof payload === 'object') {
        const payloadStr = JSON.stringify(payload);
        if (payloadStr.includes('temp_')) {
          const mappings = await offlineDb.idMappings.toArray();
          let resolved = payloadStr;
          for (const m of mappings) {
            if (resolved.includes(m.tempId)) {
              resolved = resolved.replaceAll(m.tempId, m.serverId);
            }
          }
          payload = JSON.parse(resolved);
        }
      }

      // Replays use the raw client request, which has no timeout of its own.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REPLAY_TIMEOUT_MS);
      let response;
      try {
        response = await originalRequest(endpoint, {
          method: item.method,
          body: JSON.stringify(payload),
          signal: controller.signal,
        }, item.methodName);
      } finally {
        clearTimeout(timer);
      }

      // If this was a create, register the ID mapping
      if (item.operationType === 'create' && item.tempId) {
        const serverId = this._extractServerId(response, item.entityType);
        if (serverId) {
          await registerMapping(item.tempId, serverId);
        }
        // For createVisit, register both encounter and note IDs
        if (item.entityType === 'visit' && response) {
          if (response.encounter?.id && item.payload._tempEncounterId) {
            await registerMapping(item.payload._tempEncounterId, response.encounter.id);
          }
          if (response.clinical_note?.id && item.payload._tempNoteId) {
            await registerMapping(item.payload._tempNoteId, response.clinical_note.id);
          }
        }
        await this._onCreateSynced(item, response);
      }

      await this._markSynced(item, response);
    } catch (error) {
      // A 400 that says the operation already applied is a success.
      const okRule = IDEMPOTENT_OK.find(r => r.when(item) && r.re.test(error.apiMessage || error.message || ''));
      if (error.status === 400 && okRule) {
        await this._markSynced(item, null);
        return;
      }

      // Backend unavailable (network error, our timeout, 5xx): the item goes
      // back to pending and THIS DRAIN STOPS, so a backend that dies mid-replay
      // costs one item one try instead of walking the whole queue into
      // 'failed'. Attempts count only when the server actually answered 5xx:
      // a payload that always 500s still ends as failed after MAX_ATTEMPTS
      // drains, while an outage (no answer at all) can never strand work.
      const backendUnavailable = isNetworkError(error) || (error.status && error.status >= 500) || error.name === 'AbortError';
      const serverAnswered = Boolean(error.status && error.status >= 500);
      const attempts = item.attempts + (backendUnavailable && !serverAnswered ? 0 : 1);

      if (backendUnavailable && attempts < MAX_ATTEMPTS) {
        await offlineDb.syncQueue.update(item.localId, {
          status: 'pending',
          attempts,
          lastError: error.message,
          // The request may have reached the server even though we saw a
          // failure — probe creates before the next drain re-sends.
          uncertain: item.operationType === 'create' ? true : item.uncertain,
          updatedAt: Date.now(),
        });
        this._halt = true;
        connectivityMonitor.reportBackendFailure();
      } else {
        await offlineDb.syncQueue.update(item.localId, {
          status: 'failed',
          attempts,
          lastError: error.message || 'Unknown error',
          uncertain: item.operationType === 'create' ? true : item.uncertain,
          updatedAt: Date.now(),
        });
        this._emit('item:failed', { localId: item.localId, error: error.message });
      }
    }
  }

  async _markSynced(item, response) {
    await offlineDb.syncQueue.update(item.localId, {
      status: 'synced',
      updatedAt: Date.now(),
      // Strip PHI: keep only the ids needed for later temp-id resolution.
      payload: null,
      serverResponse: response ? {
        id: response.id ?? null,
        encounter_id: response.encounter?.id ?? null,
        clinical_note_id: response.clinical_note?.id ?? null,
      } : null,
    });
    this._emit('item:synced', { localId: item.localId, entityId: item.entityId, tempId: item.tempId });
  }

  // After a create syncs, swap the temp-id cache row for the server row so
  // later reads (and coalesced PATCH targets) use real ids.
  async _onCreateSynced(item, response) {
    try {
      if (item.entityType === 'flowSheetEntry' && response?.id) {
        await offlineDb.flowSheetEntries.delete(item.tempId);
        await offlineDb.flowSheetEntries.put({ ...response, _cachedAt: Date.now() });
      } else if (item.entityType === 'visit' && response) {
        if (item.payload?._tempEncounterId && response.encounter?.id) {
          await offlineDb.encounters.delete(item.payload._tempEncounterId);
          await offlineDb.encounters.put({ ...response.encounter, _cachedAt: Date.now() });
        }
        if (item.payload?._tempNoteId && response.clinical_note?.id) {
          await offlineDb.clinicalNotes.delete(item.payload._tempNoteId);
          await offlineDb.clinicalNotes.put({ ...response.clinical_note, _cachedAt: Date.now() });
        }
      } else if (item.entityType === 'invoice' && response?.id) {
        await offlineDb.invoices.delete(item.tempId);
        await offlineDb.invoices.put({ ...response, _cachedAt: Date.now() });
      }
    } catch (e) {
      console.warn('[SyncQueue] cache swap after create failed:', e.message);
    }
  }

  // Look for a row the server may already hold for an uncertain create.
  // Returns the server record (→ mark synced) or null (→ safe to send).
  // A probe failure returns null: the normal retry path takes over.
  async _probeExisting(item, originalRequest) {
    try {
      if (item.entityType === 'visit' && item.payload?.appointment_id) {
        // POST /visit stamps encounter_id onto the appointment.
        const appt = await originalRequest(`/appointment/${item.payload.appointment_id}`, { method: 'GET' }, 'syncProbe');
        if (appt?.encounter_id) {
          if (item.payload._tempEncounterId) await registerMapping(item.payload._tempEncounterId, appt.encounter_id);
          if (item.tempId) await registerMapping(item.tempId, appt.encounter_id);
          if (item.payload._tempNoteId) {
            const notes = await originalRequest(`/clinical_note?encounter_id=${appt.encounter_id}`, { method: 'GET' }, 'syncProbe');
            const note = Array.isArray(notes) ? notes[0] : notes?.items?.[0];
            if (note?.id) await registerMapping(item.payload._tempNoteId, note.id);
          }
          return { id: appt.encounter_id };
        }
        return null;
      }

      if (item.entityType === 'flowSheetEntry') {
        const noteId = await resolveId(item.payload?.clinical_note_id);
        if (!noteId || String(noteId).startsWith('temp_')) return null;
        const params = new URLSearchParams({ clinical_note_id: noteId, limit: '200' });
        const result = await originalRequest(`/flow-sheet-entry?${params}`, { method: 'GET' }, 'syncProbe');
        const items = result?.items || [];
        const side = item.payload?.side || '';
        const match = items.find(e =>
          e.flow_item_id === item.payload?.flow_item_id &&
          (e.side || '') === side &&
          (item.payload?.data_type !== 'exercise' ||
            (String(e.exercise_id || '') === String(item.payload?.exercise_id || '') && e.label === item.payload?.label))
        );
        if (match) {
          if (item.tempId) await registerMapping(item.tempId, match.id);
          return match;
        }
        return null;
      }
    } catch (e) {
      console.warn('[SyncQueue] duplicate probe failed, will retry normally:', e.message);
    }
    return null;
  }

  async _dependencyState(dependsOnTempId) {
    const mapping = await offlineDb.idMappings.get(dependsOnTempId);
    if (mapping) return 'resolved';

    const depItem = await offlineDb.syncQueue
      .where('tempId').equals(dependsOnTempId)
      .first();
    if (!depItem) return 'missing';
    if (depItem.status === 'synced') return 'resolved';
    if (depItem.status === 'failed') return 'failed';
    return 'unresolved';
  }

  async _remapEndpoint(endpoint, dependsOn) {
    if (!endpoint.includes('temp_')) return endpoint;

    if (dependsOn) {
      const resolvedId = await resolveId(dependsOn);
      if (resolvedId !== dependsOn) {
        return endpoint.replace(dependsOn, resolvedId);
      }
    }

    const mappings = await offlineDb.idMappings.toArray();
    let result = endpoint;
    for (const m of mappings) {
      if (result.includes(m.tempId)) {
        result = result.replace(m.tempId, m.serverId);
      }
    }
    return result;
  }

  _extractServerId(response, entityType) {
    if (!response) return null;
    if (entityType === 'visit') return response.encounter?.id || response.id;
    return response.id;
  }

  // Synced rows are kept briefly for the entity-status badges, then removed
  // (their payloads are already nulled at sync time).
  async pruneSyncQueue() {
    const now = Date.now();
    try {
      const staleSynced = await offlineDb.syncQueue
        .where('status').equals('synced')
        .filter(i => (i.updatedAt || 0) < now - SYNCED_RETENTION_MS)
        .primaryKeys();
      if (staleSynced.length) await offlineDb.syncQueue.bulkDelete(staleSynced);

      const staleMappings = await offlineDb.idMappings
        .filter(m => (m.createdAt || 0) < now - MAPPING_RETENTION_MS)
        .primaryKeys();
      if (staleMappings.length) await offlineDb.idMappings.bulkDelete(staleMappings);
    } catch {
      // non-critical
    }
  }

  async retryFailed() {
    await offlineDb.syncQueue
      .where('status').equals('failed')
      .modify({ status: 'pending', attempts: 0, lastError: null });
    this._emit('queue:retry', {});
  }

  async retryItem(localId) {
    await offlineDb.syncQueue.update(localId, { status: 'pending', attempts: 0, lastError: null });
  }

  async getPendingCount() {
    return offlineDb.syncQueue.where('status').equals('pending').count();
  }

  async getFailedCount() {
    return offlineDb.syncQueue.where('status').equals('failed').count();
  }

  async getFailedItems() {
    return offlineDb.syncQueue.where('status').equals('failed').toArray();
  }

  async getStatusForEntity(entityId) {
    if (!entityId) return null;
    const items = await offlineDb.syncQueue
      .where('entityId').equals(entityId)
      .toArray();

    if (!items.length) {
      // Check by tempId
      const byTemp = await offlineDb.syncQueue
        .where('tempId').equals(entityId)
        .toArray();
      if (!byTemp.length) return null;
      return this._aggregateStatus(byTemp);
    }

    return this._aggregateStatus(items);
  }

  _aggregateStatus(items) {
    if (items.some(i => i.status === 'failed')) return { status: 'failed', lastError: items.find(i => i.status === 'failed')?.lastError };
    if (items.some(i => i.status === 'in_progress')) return { status: 'in_progress' };
    if (items.some(i => i.status === 'pending')) return { status: 'pending' };
    return { status: 'synced' };
  }

  get isProcessing() {
    return this._processing;
  }
}

export const syncQueueService = new SyncQueueService();
